import { useState } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import CampaignMessageModal from '../components/CampaignMessageModal';
import CampaignTable from '../components/CampaignTable';
import ConfirmModal from '../components/ConfirmModal';
import NoticePanel from '../components/NoticePanel';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import type {
  Campaign,
  CampaignCsvLink,
  CampaignPage,
} from '../types/whatsapp';

const CAMPAIGNS_QUERY_KEY = ['whatsapp-campaigns'];

function uniqueByCampaignId(campaigns: Campaign[]): Campaign[] {
  const seenIds = new Set<string>();
  return campaigns.filter((campaign) => {
    if (seenIds.has(campaign.campaign_id)) return false;
    seenIds.add(campaign.campaign_id);
    return true;
  });
}

function cancelWarningText(campaign: Campaign): string {
  const name = campaign.campaign_name || campaign.template_name || 'comms';
  return `Stop "${name}" before it goes out? The recipients sheet and the record are kept. Once a comms has started sending it can no longer be stopped.`;
}

interface CampaignsShellProps {
  children: React.ReactNode;
  isRefreshing: boolean;
  onRefresh: () => void;
}

function CampaignsShell({
  children,
  isRefreshing,
  onRefresh,
}: CampaignsShellProps) {
  return (
    <div className="flex h-screen flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            WhatsApp Comms
          </h1>
        </div>
        <button
          type="button"
          onClick={() => onRefresh()}
          disabled={isRefreshing}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      <div className="flex-1 overflow-auto p-6">{children}</div>
    </div>
  );
}

export default function WhatsappCampaigns() {
  const [campaignToCancel, setCancelTarget] = useState<Campaign | null>(null);
  const [campaignToView, setCampaignToView] = useState<Campaign | null>(null);

  const campaignsQuery = useInfiniteQuery({
    queryKey: CAMPAIGNS_QUERY_KEY,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const page: CampaignPage = await axios.get(
        endpoints.whatsapp.campaigns(pageParam),
      );
      return page;
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const cancelMutation = useMutation({
    mutationFn: (campaignId: string) =>
      axios.post(endpoints.whatsapp.cancelCampaign(campaignId)),
    onSuccess: () => {
      toast.success('Comms cancelled.');
      setCancelTarget(null);
      campaignsQuery.refetch();
    },
  });

  const csvMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const link: CampaignCsvLink = await axios.get(
        endpoints.whatsapp.campaignCsv(campaignId),
      );
      return link;
    },
    onSuccess: (link) => {
      const sheetLink = document.createElement('a');
      sheetLink.href = link.url;
      sheetLink.click();
      toast.success('Downloading the recipients sheet.');
    },
  });

  const campaigns = uniqueByCampaignId(
    campaignsQuery.data?.pages.flatMap((page) => page.campaigns) ?? [],
  );
  const isRefreshing =
    campaignsQuery.isFetching && !campaignsQuery.isFetchingNextPage;

  const confirmCancel = () => {
    if (!campaignToCancel || cancelMutation.isPending) return;
    cancelMutation.mutate(campaignToCancel.campaign_id);
  };

  const renderCampaignsBody = () => {
    if (campaignsQuery.isPending) {
      return <div className="text-gray-500">Loading...</div>;
    }

    if (campaignsQuery.isError && campaigns.length === 0) {
      return (
        <NoticePanel
          title="Could not load your comms"
          actionLabel="Try again"
          onAction={() => campaignsQuery.refetch()}
        >
          The messaging service did not answer, so we cannot tell you what has
          been sent. Nothing has been lost — try again in a moment.
        </NoticePanel>
      );
    }

    if (campaigns.length === 0 && !campaignsQuery.hasNextPage) {
      return (
        <NoticePanel title="No comms yet">
          Bulk sends show up here as soon as you start one. Head to{' '}
          <Link to="/whatsapp-bulk-send" className="text-blue-600 underline">
            Bulk Send
          </Link>{' '}
          to send your first comms.
        </NoticePanel>
      );
    }

    return (
      <>
        <CampaignTable
          campaigns={campaigns}
          cancellingCampaignId={
            cancelMutation.isPending ? (cancelMutation.variables ?? null) : null
          }
          fetchingCsvCampaignId={
            csvMutation.isPending ? (csvMutation.variables ?? null) : null
          }
          onCancel={setCancelTarget}
          onDownloadCsv={csvMutation.mutate}
          onView={setCampaignToView}
        />
        {campaignsQuery.hasNextPage && (
          <button
            type="button"
            onClick={() => campaignsQuery.fetchNextPage()}
            disabled={campaignsQuery.isFetchingNextPage}
            className="mt-4 text-sm font-light text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            {campaignsQuery.isFetchingNextPage
              ? 'Loading older comms...'
              : 'Load older comms'}
          </button>
        )}
      </>
    );
  };

  return (
    <CampaignsShell
      isRefreshing={isRefreshing}
      onRefresh={campaignsQuery.refetch}
    >
      {renderCampaignsBody()}
      <CampaignMessageModal
        campaign={campaignToView}
        onClose={() => setCampaignToView(null)}
      />
      <ConfirmModal
        isOpen={campaignToCancel !== null}
        title="Cancel this comms"
        message={campaignToCancel ? cancelWarningText(campaignToCancel) : ''}
        confirmLabel={
          cancelMutation.isPending ? 'Stopping...' : 'Stop the send'
        }
        cancelLabel="Keep it"
        onConfirm={confirmCancel}
        onCancel={() => setCancelTarget(null)}
        destructive
        isBusy={cancelMutation.isPending}
      />
    </CampaignsShell>
  );
}
